/**
 * Custom Logger Utility
 * 
 * Environment-aware logging that only outputs in development mode
 * Usage: const logger = new Logger('ModuleName');
 *        logger.log('message', { data: 'here' });
 */

class Logger {
    constructor(moduleName = 'App') {
        this.moduleName = moduleName;
        this.isDevelopment = this.detectEnvironment();
    }

    /**
     * Detect if we're in development or production
     * Checks for data-environment on HTML element, window.ENV, or defaults to development
     */
    detectEnvironment() {
        // Check HTML element data attribute
        const htmlElement = document.documentElement;
        if (htmlElement && htmlElement.dataset.environment) {
            return htmlElement.dataset.environment !== 'production';
        }

        // Check window.ENV
        if (window.ENV) {
            return window.ENV !== 'production';
        }

        // Default to development (safe)
        return true;
    }

    /**
     * Get module prefix with timestamp
     */
    getPrefix() {
        const now = new Date();
        const time = now.toLocaleTimeString('en-GB', { 
            hour12: false,
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            fractionalSecondDigits: 3
        });
        return `[${time}] [${this.moduleName}]`;
    }

    /**
     * Log message (only in development)
     */
    log(message, data = null) {
        if (!this.isDevelopment) return;

        if (data) {
            console.log(`${this.getPrefix()} ${message}`, data);
        } else {
            console.log(`${this.getPrefix()} ${message}`);
        }
    }

    /**
     * Log warning (always shown, prefixed)
     */
    warn(message, data = null) {
        if (data) {
            console.warn(`${this.getPrefix()} ⚠️ ${message}`, data);
        } else {
            console.warn(`${this.getPrefix()} ⚠️ ${message}`);
        }
    }

    /**
     * Log error (always shown, prefixed)
     */
    error(message, data = null) {
        if (data) {
            console.error(`${this.getPrefix()} ❌ ${message}`, data);
        } else {
            console.error(`${this.getPrefix()} ❌ ${message}`);
        }
    }

    /**
     * Log info (development only, with ℹ️ icon)
     */
    info(message, data = null) {
        if (!this.isDevelopment) return;

        if (data) {
            console.info(`${this.getPrefix()} ℹ️ ${message}`, data);
        } else {
            console.info(`${this.getPrefix()} ℹ️ ${message}`);
        }
    }

    /**
     * Log success (development only, with ✓ icon)
     */
    success(message, data = null) {
        if (!this.isDevelopment) return;

        if (data) {
            console.log(`${this.getPrefix()} ✓ ${message}`, data);
        } else {
            console.log(`${this.getPrefix()} ✓ ${message}`);
        }
    }

    /**
     * Log debug table (development only)
     */
    table(data) {
        if (!this.isDevelopment) return;
        console.table(data);
    }
}

export default Logger;
